import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { armTapShield } from '../ActionButton';
import {
  addSampleEntry,
  answerProgress,
  answeredCount,
  findMissingRequired,
  getSampleEntries,
  removeSampleEntry,
  sectionProgress,
  setAnswer,
  setSampleField,
} from '../../audit/answers';
import {
  isQuestionRequired,
  isQuestionVisible,
  isSectionVisible,
  subsectionTitlesFor,
  type AnswerMap,
  type AnswerValue,
  type Question,
  type Section,
  type TemplateDefinition,
} from '../../audit/template';
import { QuestionInput } from './QuestionInput';
import { SampleGroupInput } from './SampleGroupInput';

/**
 * Generic template renderer (spec §4): renders ANY valid TemplateDefinition
 * — adding an audit type must require zero code changes here. Controlled:
 * the owner supplies answers + onChange, so the conduct page (IndexedDB),
 * the builder preview and the Form preview (memory) all plug in unchanged.
 *
 * Navigation for long forms: a sticky section jump bar with per-section
 * completion, plus a "next unanswered" jump. No live score here — score
 * visibility is a page-level, role-based concern (scope.ts).
 */
export function AuditForm({
  definition,
  answers,
  onAnswersChange,
  onSubmit,
  submitLabel = 'Submit audit',
  questionExtras,
  questionActions,
  questionHint,
  onEntryRemoved,
  statusNotice,
}: {
  definition: TemplateDefinition;
  answers: AnswerMap;
  onAnswersChange: (next: AnswerMap, changedQuestionId: string) => void;
  onSubmit?: (answers: AnswerMap) => void;
  submitLabel?: string;
  /**
   * Content rendered below a question (notes list, editors). For sample
   * groups the callback also fires per entry with that entry's index.
   */
  questionExtras?:
    | ((questionId: string, sampleIndex?: number) => import('react').ReactNode)
    | undefined;
  /** Compact actions anchored right of the question text (badge buttons). */
  questionActions?:
    | ((questionId: string, sampleIndex?: number) => import('react').ReactNode)
    | undefined;
  /**
   * A line under a question's input (B8: the store's last KPI value, its
   * budget and the "Big change" warning). Not called for sample groups.
   */
  questionHint?: ((question: Question, value: AnswerValue | undefined) => import('react').ReactNode) | undefined;
  /** A sample entry was deleted — lets the owner renumber attached items. */
  onEntryRemoved?: ((questionId: string, sampleIndex: number) => void) | undefined;
  /**
   * Always-visible status line (e.g. the expiry countdown): rendered in the
   * sticky desktop sidebar and the sticky mobile toolbar.
   */
  statusNotice?: import('react').ReactNode;
}) {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [sectionsOpen, setSectionsOpen] = useState(false);
  // The sections list closes on a scroll, a tap outside it or Escape (UI review U229: on a touch
  // phone it stayed open over the questions and caught the taps meant for them).
  const toolbarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sectionsOpen) return;
    const close = () => setSectionsOpen(false);
    const outside = (e: PointerEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) close();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', close, { passive: true });
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('scroll', close);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [sectionsOpen]);

  const missing = useMemo(
    () => findMissingRequired(definition, answers),
    [definition, answers],
  );
  const progress = useMemo(
    () => answerProgress(definition, answers),
    [definition, answers],
  );
  const answered = useMemo(() => answeredCount(definition, answers), [definition, answers]);
  // Tag the minority (UI review 3, D8 / U184): mostly optional (the DM Visit, one required
  // gate) → mark the required ones; mostly required → mark the optional ones; none required → no tags.
  const tagMode = useMemo((): TagMode => {
    let required = 0;
    let optional = 0;
    for (const s of definition.sections) {
      if (!isSectionVisible(definition, s, answers)) continue;
      for (const q of s.questions) {
        if (!isQuestionVisible(definition, s, q, answers)) continue;
        if (isQuestionRequired(definition, q, answers)) required++;
        else optional++;
      }
    }
    return required === 0 ? 'none' : required < optional ? 'required' : 'optional';
  }, [definition, answers]);
  const perSection = useMemo(
    () => sectionProgress(definition, answers),
    [definition, answers],
  );

  const missingByQuestion = useMemo(() => {
    if (!submitAttempted) return new Map<string, Set<string>>();
    const map = new Map<string, Set<string>>();
    for (const m of missing) {
      const set = map.get(m.questionId) ?? new Set<string>();
      if (m.needsEntries !== undefined) set.add('#count');
      else if (m.sampleIndex !== undefined) set.add(`${m.sampleIndex}:${m.fieldId}`);
      else set.add('*');
      map.set(m.questionId, set);
    }
    return map;
  }, [missing, submitAttempted]);

  const shortfallFor = (questionId: string): number =>
    missing.find((m) => m.questionId === questionId && m.needsEntries !== undefined)
      ?.needsEntries ?? 0;

  const jumpToSection = (sectionId: string) => {
    setSectionsOpen(false);
    setCollapsed((c) => ({ ...c, [sectionId]: false }));
    // Defer until the section body exists in the DOM.
    requestAnimationFrame(() => {
      document
        .getElementById(`sec-${sectionId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const jumpToNextUnanswered = () => {
    const first = missing[0];
    if (!first) return;
    setCollapsed((c) => ({ ...c, [first.sectionId]: false }));
    requestAnimationFrame(() => {
      const el =
        document.getElementById(`q-${first.questionId}`) ??
        document.getElementById(`sec-${first.sectionId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const trySubmit = (e: { detail: number; timeStamp: number }) => {
    // The review replaces the form: a double tap's second tap must not land
    // on "Confirm & submit" where this button was.
    armTapShield(e);
    setSubmitAttempted(true);
    if (missing.length === 0) onSubmit?.(answers);
    else jumpToNextUnanswered();
  };

  const progressBar = (
    <div
      className="audit-progress"
      title={progress.total > 0 ? `Required answers completed · ${answered} answered in all` : `${answered} answered — nothing on this form is required`}
    >
      <div
        className={progress.total > 0 && progress.answered >= progress.total ? 'audit-progress-bar audit-progress-done' : 'audit-progress-bar'}
        style={{
          width: `${progress.total ? (progress.answered / progress.total) * 100 : 0}%`,
        }}
      />
      <span className="audit-progress-text">
        {/* Say what's counted (final run: "1 / 1" after filling 28 fields read as no progress). */}
        {progress.total > 0 ? `${progress.answered} / ${progress.total} required answered` : `${answered} answered · every question is optional`}
      </span>
    </div>
  );

  const sectionRows = definition.sections.map((s, i) => {
    if (!isSectionVisible(definition, s, answers)) return null;
    const p = perSection[s.id] ?? { answered: 0, total: 0 };
    const done = p.total > 0 && p.answered >= p.total;
    return (
      <button
        type="button"
        key={s.id}
        className="section-browser-row"
        onClick={() => jumpToSection(s.id)}
      >
        <span className="section-browser-title">
          {i + 1}. {s.title}
        </span>
        <span
          className={done ? 'section-browser-count done' : 'section-browser-count'}
        >
          {/* Nothing required here (round 5: a visit's optional sections read "0/0"). */}
          {done ? '✓' : p.total === 0 ? '' : `${p.answered}/${p.total}`}
        </span>
      </button>
    );
  });

  const nextButton = missing.length > 0 && (
    <button type="button" className="jump jump-next" onClick={jumpToNextUnanswered}>
      Next unanswered ↓
    </button>
  );

  return (
    <div className="audit-layout">
      {/* Desktop: persistent sidebar with per-section progress. */}
      <aside className="audit-sidenav" aria-label="Sections">
        {statusNotice}
        {progressBar}
        <nav className="sidenav-rows">{sectionRows}</nav>
        {nextButton && <div className="sidenav-foot">{nextButton}</div>}
      </aside>

      <div className="audit-form">
        {/* Mobile: sticky toolbar; sections collapse into the sheet. */}
        <div className="audit-toolbar" ref={toolbarRef}>
          {statusNotice}
          {progressBar}
          <nav className="audit-jumpbar" aria-label="Sections">
            <button
              type="button"
              className="jump jump-menu"
              aria-expanded={sectionsOpen}
              onClick={() => setSectionsOpen((o) => !o)}
            >
              ☰ Sections
            </button>
            {nextButton}
          </nav>
          {sectionsOpen && (
            <div className="section-browser" role="menu" aria-label="Go to section">
              {sectionRows}
            </div>
          )}
        </div>

        {definition.sections.map((section) =>
          isSectionVisible(definition, section, answers) ? (
            <SectionCard
              key={section.id}
              definition={definition}
              section={section}
              answers={answers}
              collapsed={collapsed[section.id] ?? false}
              onToggle={() =>
                setCollapsed((c) => ({ ...c, [section.id]: !(c[section.id] ?? false) }))
              }
              progress={perSection[section.id] ?? { answered: 0, total: 0 }}
              tagMode={tagMode}
              missingByQuestion={missingByQuestion}
              shortfallFor={shortfallFor}
              onAnswersChange={onAnswersChange}
              questionExtras={questionExtras}
              questionActions={questionActions}
              questionHint={questionHint}
              onEntryRemoved={onEntryRemoved}
            />
          ) : null,
        )}

        {onSubmit && (
          <div className="audit-submit">
            {submitAttempted && missing.length > 0 && (
              <p className="error">
                {missing.length} required answer{missing.length > 1 ? 's' : ''}{' '}
                still missing — highlighted above.
              </p>
            )}
            <button className="btn btn-primary" onClick={trySubmit}>
              {submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionCard({
  definition,
  section,
  answers,
  collapsed,
  onToggle,
  progress,
  tagMode,
  missingByQuestion,
  shortfallFor,
  onAnswersChange,
  questionExtras,
  questionActions,
  questionHint,
  onEntryRemoved,
}: {
  definition: TemplateDefinition;
  section: Section;
  answers: AnswerMap;
  collapsed: boolean;
  onToggle: () => void;
  /** Which questions carry a tag (UI review 3, D8 / U184): the minority. */
  tagMode: TagMode;
  progress: { answered: number; total: number };
  missingByQuestion: ReadonlyMap<string, ReadonlySet<string>>;
  shortfallFor: (questionId: string) => number;
  onAnswersChange: (next: AnswerMap, changedQuestionId: string) => void;
  questionExtras?:
    | ((questionId: string, sampleIndex?: number) => import('react').ReactNode)
    | undefined;
  questionActions?:
    | ((questionId: string, sampleIndex?: number) => import('react').ReactNode)
    | undefined;
  questionHint?: ((question: Question, value: AnswerValue | undefined) => import('react').ReactNode) | undefined;
  onEntryRemoved?: ((questionId: string, sampleIndex: number) => void) | undefined;
}) {
  const done = progress.total > 0 && progress.answered >= progress.total;
  const titles = subsectionTitlesFor(
    section,
    section.questions.filter((q) => isQuestionVisible(definition, section, q, answers)),
  );

  return (
    <section className="audit-section" id={`sec-${section.id}`}>
      <button
        type="button"
        className="audit-section-head"
        aria-expanded={!collapsed}
        onClick={onToggle}
      >
        <span>
          {section.title}{' '}
          <span className={done ? 'section-count done' : 'section-count'}>
            {done ? '✓' : progress.total === 0 ? '' : `${progress.answered}/${progress.total}`}
          </span>
        </span>
        <span className="audit-section-chevron" aria-hidden>
          {collapsed ? '▸' : '▾'}
        </span>
      </button>
      {!collapsed && (
        <div className="audit-section-body">
          {section.instruction && (
            <p className="audit-instruction">{section.instruction}</p>
          )}
          {section.questions.map((q) =>
            isQuestionVisible(definition, section, q, answers) ? (
              <Fragment key={q.id}>
                {/* Subsection titles (owner ruling 2026-11-02): above the first shown question of each subsection. */}
                {titles.get(q.id) && <h3 className="q-subsection">{titles.get(q.id)}</h3>}
              <QuestionRow
                question={q}
                required={isQuestionRequired(definition, q, answers)}
                tagMode={tagMode}
                answers={answers}
                missing={missingByQuestion.get(q.id)}
                entryShortfall={shortfallFor(q.id)}
                onAnswersChange={onAnswersChange}
                extrasFor={questionExtras}
                actionsFor={questionActions}
                hintFor={questionHint}
                onEntryRemoved={onEntryRemoved}
              />
              </Fragment>
            ) : null,
          )}
          {section.notesField && (
            <div className="q-row">
              <label className="q-text muted">Section notes</label>
              <textarea
                className="q-input"
                rows={2}
                value={
                  (answers[`${section.id}__notes`] as string | undefined) ?? ''
                }
                onChange={(e) =>
                  onAnswersChange(
                    setAnswer(
                      answers,
                      `${section.id}__notes`,
                      e.target.value || undefined,
                    ),
                    `${section.id}__notes`,
                  )
                }
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** A second "×" within this long after a removal is the same double tap. */
const REMOVE_GAP_MS = 700;

function QuestionRow({
  question,
  required,
  tagMode,
  answers,
  missing,
  entryShortfall,
  onAnswersChange,
  extrasFor,
  actionsFor,
  hintFor,
  onEntryRemoved,
}: {
  question: Question;
  /** Right now — its own setting plus any met Require / Not require rule. */
  required: boolean;
  tagMode: TagMode;
  answers: AnswerMap;
  missing: ReadonlySet<string> | undefined;
  entryShortfall: number;
  onAnswersChange: (next: AnswerMap, changedQuestionId: string) => void;
  extrasFor?:
    | ((questionId: string, sampleIndex?: number) => import('react').ReactNode)
    | undefined;
  actionsFor?:
    | ((questionId: string, sampleIndex?: number) => import('react').ReactNode)
    | undefined;
  hintFor?: ((question: Question, value: AnswerValue | undefined) => import('react').ReactNode) | undefined;
  onEntryRemoved?: ((questionId: string, sampleIndex: number) => void) | undefined;
}) {
  const isMissing = missing?.has('*') ?? false;
  // When "×" last removed an entry: the next entry's "×" moves up under the
  // finger, so the second tap of a double tap (a separate event, after the
  // re-render) would remove — and re-number the notes/photos of — that entry
  // too. A removal within REMOVE_GAP_MS of the last one is ignored.
  const lastRemoveAt = useRef(0);
  const extras = extrasFor?.(question.id);
  const actions = actionsFor?.(question.id);
  /** "More detail" (Question.heading — owner ruling 2026-11-02); blank / spaces = none. */
  const detail = question.heading?.trim() ?? '';

  if (question.type === 'sample_group') {
    return (
      <div className="q-row" id={`q-${question.id}`}>
        <div className="q-head">
          {/* The question and its "More detail" stay together, left of the Note / Task / Upload buttons (UI review 2026-11-02). */}
          <div className="q-main">
            <label className="q-text">
              {question.text}
              <RequiredTag required={required} mode={tagMode} />
            </label>
            {detail && (
              <p className="q-detail" id={`q-detail-${question.id}`}>
                {detail}
              </p>
            )}
          </div>
          {actions && <span className="q-actions">{actions}</span>}
        </div>
        <SampleGroupInput
          question={question}
          answers={answers}
          missing={missing ?? new Set()}
          entryShortfall={entryShortfall}
          onFieldChange={(i, fieldId, value) =>
            onAnswersChange(setSampleField(answers, question.id, i, fieldId, value), question.id)
          }
          onAddEntry={() =>
            onAnswersChange(
              addSampleEntry(answers, question.id, question.sampleCount ?? 0),
              question.id,
            )
          }
          onRemoveEntry={(i) => {
            if (Date.now() - lastRemoveAt.current < REMOVE_GAP_MS) return;
            const entry = getSampleEntries(answers, question.id)[i];
            const hasContent =
              entry && Object.values(entry).some((v) => v && v.trim() !== '');
            if (
              !hasContent ||
              window.confirm(`Remove entry ${i + 1}? Its answers will be lost.`)
            ) {
              lastRemoveAt.current = Date.now();
              onAnswersChange(removeSampleEntry(answers, question.id, i), question.id);
              onEntryRemoved?.(question.id, i);
            }
          }}
          entryActions={actionsFor ? (i) => actionsFor(question.id, i) : undefined}
          entryExtras={extrasFor ? (i) => extrasFor(question.id, i) : undefined}
        />
        {extras}
      </div>
    );
  }

  // The question text names its input (round 1 2026-10-21: screen readers
  // and getByLabel couldn't find "Occupancy %"): every input names it via
  // aria-labelledby — NOT <label htmlFor>, which made a tap on the question
  // text pop the phone keyboard / date picker (QA review 2026-10-21).
  const inputId = `q-input-${question.id}`;
  const labelId = `q-label-${question.id}`;
  return (
    <div className={isMissing ? 'q-row missing' : 'q-row'} id={`q-${question.id}`}>
      <div className="q-head">
        <div className="q-main">
          <label className="q-text" id={labelId}>
            {question.text}
            <RequiredTag required={required} mode={tagMode} />
          </label>
          {detail && (
            <p className="q-detail" id={`q-detail-${question.id}`}>
              {detail}
            </p>
          )}
        </div>
        {actions && <span className="q-actions">{actions}</span>}
      </div>
      <QuestionInput
        question={question}
        inputId={inputId}
        labelId={labelId}
        {...(detail ? { describedBy: `q-detail-${question.id}` } : {})}
        value={answers[question.id]}
        onChange={(value: AnswerValue | undefined) =>
          onAnswersChange(setAnswer(answers, question.id, value), question.id)
        }
      />
      {hintFor?.(question, answers[question.id])}
      {extras}
    </div>
  );
}

/** 'optional' = tag the optional questions; 'required' = tag the required ones; 'none' = no tags. */
type TagMode = 'optional' | 'required' | 'none';

function RequiredTag({ required, mode }: { required: boolean; mode: TagMode }) {
  if (mode === 'optional' && !required) return <span className="muted"> (optional)</span>;
  if (mode === 'required' && required) return <span className="q-required-tag"> (required)</span>;
  return null;
}
